import React, { useState } from 'react';
import { Button, Typography } from 'antd';
// Helpers
import { sortDeliverablesChecklistByType } from 'helpers/timelineDeliverables';
import { mapUgcEditingDeliverableToFormData } from 'helpers/campaignUgc';
import { TimeLineDeliverable } from 'helpers/deliverables';
import { getCategory } from 'helpers/customCampaign';
// Types
import {
  CampaignSmPlatform,
  CreateCampaignTaskV2Input,
} from 'api/graphql-global-types';
import type { AnyTask, ExampleFile } from 'helpers/customCampaign';
import {
  GetCampaignsV2_getCampaigns_entities_activities_UgcActivity_tasks,
  GetCampaignsV2_getCampaigns_entities_activities_CustomBriefActivity_tasksV2_CampaignAppearanceTask,
} from 'api/campaignV2/types/GetCampaignsV2';
// Components
import TimelineDeliverablesModal from 'pages/CampaignDetails/CampaignDetails/components/TimelineDeliverablesModal/TimelineDeliverablesModal';
import CustomContentDeliverableModal from './components/CustomContentDeliverableModal/CustomContentDeliverableModal';
import AppearanceDeliverableModal from './components/AppearanceDeliverableModal/AppearanceDeliverableModal';
import CustomDeliverableItem from './components/CustomDeliverableItem/CustomDeliverableItem';
// Utils
import { CustomDeliverableCategory } from 'helpers/customCampaignConstants';
// Styles
import styles from './CustomDeliverables.module.scss';

type CustomDeliverablesProps = {
  deliverablesChecklist: TimeLineDeliverable[];
  setDeliverablesChecklist: React.Dispatch<
    React.SetStateAction<TimeLineDeliverable[]>
  >;
  /** Platforms selected for the campaign — drives the Social deliverable platform picker. */
  availablePlatforms?: CampaignSmPlatform[];
  error?: string | null;
};

const CustomDeliverables: React.FC<CustomDeliverablesProps> = ({
  deliverablesChecklist,
  setDeliverablesChecklist,
  availablePlatforms,
  error,
}) => {
  // Social deliverables only support IG/TT; fall back to both when the campaign
  // has no IG/TT platform selected so the admin can still add a social post.
  const socialPlatforms =
    availablePlatforms?.filter((p) =>
      [CampaignSmPlatform.IG, CampaignSmPlatform.TT].includes(p)
    ) ?? [];
  const deliverablePlatforms = socialPlatforms.length
    ? socialPlatforms
    : [CampaignSmPlatform.IG, CampaignSmPlatform.TT];
  const [activeCategory, setActiveCategory] =
    useState<CustomDeliverableCategory | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingItem, setEditingItem] = useState<AnyTask | null>(null);

  const closeModal = () => {
    setActiveCategory(null);
    setEditingIndex(null);
    setEditingItem(null);
  };

  const handleSave = (data: TimeLineDeliverable) => {
    if (editingIndex !== null) {
      setDeliverablesChecklist((prev) =>
        prev.map((item, index) =>
          index === editingIndex
            ? ({
                ...data,
                examples: (data as any).examples ?? (item as any).examples,
              } as TimeLineDeliverable)
            : item
        )
      );
    } else {
      setDeliverablesChecklist((prev) =>
        sortDeliverablesChecklistByType([...prev, data])
      );
    }
    closeModal();
  };

  const handleAdd = (category: CustomDeliverableCategory) => {
    setEditingIndex(null);
    setEditingItem(null);
    setActiveCategory(category);
  };

  const handleEdit = (index: number, task: AnyTask) => {
    setEditingIndex(index);
    setEditingItem(task);
    setActiveCategory(getCategory(task));
  };

  const handleRemove = (index: number) => {
    setDeliverablesChecklist((prev) => prev.filter((_, i) => i !== index));
  };

  const handleExamplesChange = (index: number, examples: ExampleFile[]) => {
    setDeliverablesChecklist((prev) =>
      prev.map((item, itemIndex) =>
        itemIndex === index
          ? ({ ...item, examples } as TimeLineDeliverable)
          : item
      )
    );
  };

  return (
    <div className={styles.root}>
      <Typography.Text type="secondary" className={styles.description}>
        Define the content, social posts, and appearances talent must deliver
        for this campaign.
      </Typography.Text>

      <div className={styles.list}>
        {deliverablesChecklist?.map((deliverable, index) => (
          <CustomDeliverableItem
            key={index}
            deliverable={deliverable as AnyTask}
            onEdit={() => handleEdit(index, deliverable as AnyTask)}
            onRemove={() => handleRemove(index)}
            onExamplesChange={(examples) =>
              handleExamplesChange(index, examples)
            }
          />
        ))}
      </div>

      <div className={styles.addButtons}>
        <Button type="dashed" onClick={() => handleAdd('Social Media')}>
          Add Social Media
        </Button>
        <Button type="dashed" onClick={() => handleAdd('UGC')}>
          Add UGC
        </Button>
        <Button type="dashed" onClick={() => handleAdd('Appearance')}>
          Add Appearance
        </Button>
      </div>

      {error && <p className={styles.error}>{error}</p>}

      <TimelineDeliverablesModal
        isOpen={activeCategory === 'Social Media'}
        onClose={closeModal}
        onSubmit={(data: CreateCampaignTaskV2Input, examples) =>
          handleSave({ ...data, examples } as unknown as TimeLineDeliverable)
        }
        platform={deliverablePlatforms[0]}
        availablePlatforms={deliverablePlatforms}
        editingItem={editingItem}
        customFlow
      />

      <CustomContentDeliverableModal
        isOpen={activeCategory === 'UGC'}
        onClose={closeModal}
        onSubmit={(data: CreateCampaignTaskV2Input) =>
          handleSave(data as unknown as TimeLineDeliverable)
        }
        editingItem={
          editingItem as GetCampaignsV2_getCampaigns_entities_activities_UgcActivity_tasks | null
        }
        mapEditingItemToFormData={mapUgcEditingDeliverableToFormData}
      />

      <AppearanceDeliverableModal
        isOpen={activeCategory === 'Appearance'}
        onClose={closeModal}
        onSubmit={(data: CreateCampaignTaskV2Input) =>
          handleSave(data as unknown as TimeLineDeliverable)
        }
        editingItem={
          editingItem as GetCampaignsV2_getCampaigns_entities_activities_CustomBriefActivity_tasksV2_CampaignAppearanceTask | null
        }
      />
    </div>
  );
};

export default CustomDeliverables;
